import { useEffect, useState } from "react";
import {
  Identifier,
  RaRecord,
  ResourceContextValue,
  Validator,
  useRecordContext,
  useResourceContext,
} from "react-admin";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import InPlaceBooleanEditor from "./fields/InPlaceBooleanEditor";
import InPlaceNumberEditor from "./fields/InPlaceNumberEditor";
import InPlaceStateEditor from "./fields/InPlaceStateEditor";
import InPlaceTextEditor from "./fields/InPlaceTextEditor";
import { ReadOnlyInPlaceEditor } from "./fields/ReadOnlyInPlaceEditor";
import InPlaceDateEditor from "./fields/InPlaceDateEditor";
import InPlaceReferenceEditor from "./fields/InPlaceReferenceEditor";
import InPlaceSelectEditor from "./fields/InPlaceSelectEditor";
import InPlaceDollarEditor from "./fields/InPlaceDollarEditor";
import InPlacePercentageEditor from "./fields/InPlacePercentageEditor";
import { DateRangeArrayField } from "./fields/DateRangeArrayField";
import { getProps } from "@/utils/helpers/columnsHelper";
import { ResourceName } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { validateInPlaceEmail } from "@/utils/validators/inPlaceValidators";
import InPlaceColorEditor from "./fields/InPlaceColorEditor";
import InPlaceReferenceArrayEditor from "./fields/InPlaceReferenceArrayEditor";
import InPlacePhoneEditor from "./fields/InPlacePhoneEditor";
import InPlaceContractBasisEditor from "./fields/InPlaceContractBasisEditor";

/**
 * Supported in-place editor types for editable grid fields.
 */
type EditorType =
  | "text"
  | "email"
  | "number"
  | "state"
  | "boolean"
  | "date"
  | "readonly"
  | "reference"
  | "referenceArray"
  | "select"
  | "dollar"
  | "percentage"
  | "dateRangeArray"
  | "color"
  | "phone"
  | "contractBasis";

type EditorFieldChoicesType = { id: string | number; name: string }[];

/**
 * Field configuration for each editable grid cell.
 */
export interface EditorField {
  /** The key used to look up metadata and bind form values */
  source: string;
  /** The editor type to use; defaults to "text" */
  type?: EditorType;
  /** Optional label to override metadata label */
  label?: string;
  /** Responsive grid size at lg breakpoint */
  lg?: number;
  /** Responsive grid size at md breakpoint */
  md?: number;
  /** Responsive grid size at sm breakpoint */
  sm?: number;
  /** Responsive grid size at xs breakpoint */
  xs?: number;
  /** Optional group label used to visually separate fields in the grid layout */
  section?: string;
  /** Source path to check against to conditionally display this field */
  showWhen?: string;
  /** If set, field is read-only with this tooltip explaining why */
  readOnlyReason?: string;

  // Only relevant for reference fields
  /** The resource name to reference (e.g. ResourceName.CLIENTS, ResourceName.VENDORS_TPAS) */
  reference?: ResourceName;
  /** The field name to display from the referenced resource (e.g. "client_legal_name", "company_name") */
  displayField?: string;
  // Filter resource by a specific id ref (e.g. "client_id_ref")
  filter?: object;
  /** Custom label function for reference autocomplete options */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionLabel?: (option: any) => string;

  // Only relevant for select fields
  /** Array of choices for select fields */
  choices?: EditorFieldChoicesType;

  /** Optional validation function(s) to apply to the field */
  validate?: Validator | Validator[];

  /** Optional Intl.NumberFormat options passed to NumberField for number-typed fields */
  numberFieldOptions?: Intl.NumberFormatOptions;
}

/**
 * Props for the EditableGrid component.
 */
interface EditableGridProps {
  /** Fields to render in the editable grid */
  fields: EditorField[];
  /** Number of columns per row; adjusts layout */
  columnsPerRow?: number;
  /** Whether the component is rendered inside a drawer (adjusts layout tightness) */
  isDrawerOpen?: boolean;
  /** Record type (e.g., ResourceName.CLAIMANTS, ResourceName.POLICIES); used to look up metadata */
  recordType: ResourceName;
  /** Optional resource override - defaults to useResourceContext() if not provided */
  resource?: string;
  /** If true, empty fields are shown by default */
  defaultShowEmptyFields?: boolean;
  /**
   * Optional explanatory note rendered under a section's header, keyed by the
   * same string used in `EditorField.section`.
   *
   * For the rare section whose fields need context to be read correctly rather
   * than just a label — e.g. the claimant's two member ids, which come from
   * different vendors and so legitimately disagree (#6515). Sections without an
   * entry render exactly as before.
   *
   * NOTE: this exists because the per-field route does not work. EditableGrid
   * passes `helperText: metaTitle` from allColumnsMetadata (see commonProps
   * below), but every in-place editor then hardcodes
   * `helperText={validate ? undefined : false}` and discards it — so the whole
   * `tooltip` corpus in that metadata file is currently unrendered. Filed
   * separately; until it is fixed, section-level copy is the only way to get an
   * explanation onto the page.
   */
  sectionDescriptions?: Record<string, string>;
}

interface EditorComponentProps {
  source: string;
  label: string;
  resource: ResourceContextValue;
  helperText?: string;
  reference?: ResourceName;
  displayField?: string;
  filter?: object;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionLabel?: (option: any) => string;
  choices?: EditorFieldChoicesType;
  readOnly?: boolean;
  validate?: Validator | Validator[];
  numberFieldOptions?: Intl.NumberFormatOptions;
}

/**
 * Maps editor types to their corresponding in-place editor components.
 */
const editorComponentMap: Record<EditorType, (props: EditorComponentProps) => JSX.Element> = {
  text: (props) => <InPlaceTextEditor {...props} showButtons />,
  email: ({ validate, ...props }) => {
    const emailValidate = validate
      ? [validateInPlaceEmail, ...(Array.isArray(validate) ? validate : [validate])]
      : validateInPlaceEmail;
    return <InPlaceTextEditor {...props} type="email" validate={emailValidate} showButtons />;
  },
  number: (props) => <InPlaceNumberEditor {...props} showButtons />,
  state: (props) => <InPlaceStateEditor {...props} showButtons />,
  boolean: (props) => <InPlaceBooleanEditor {...props} showButtons />,
  date: (props) => <InPlaceDateEditor {...props} showButtons />,
  readonly: (props) => <ReadOnlyInPlaceEditor {...props} />,
  reference: (props) => <InPlaceReferenceEditor {...props} />,
  referenceArray: (props) => <InPlaceReferenceArrayEditor {...props} />,
  select: (props) => <InPlaceSelectEditor {...props} showButtons />,
  dollar: (props) => <InPlaceDollarEditor {...props} showButtons />,
  percentage: (props) => <InPlacePercentageEditor {...props} showButtons />,
  dateRangeArray: (props) => <DateRangeArrayField {...props} />,
  color: (props) => <InPlaceColorEditor {...props} showButtons />,
  phone: (props) => <InPlacePhoneEditor {...props} showButtons />,
  // #7320 / ADR-081 D5.2a: two-number N/M editor; the API stores the canonical string.
  contractBasis: (props) => <InPlaceContractBasisEditor {...props} showButtons />,
};

/**
 * Helper function to get a value from a nested object path.
 * @param obj - The object to traverse
 * @param path - Dot-separated path (e.g., "user.profile.name")
 * @returns The value at the path, or undefined if not found
 */
const getByPath = (obj?: RaRecord<Identifier>, path?: string) =>
  !path ? undefined : path.split(".").reduce((acc, k) => (acc === null || acc === undefined ? acc : acc[k]), obj);

/**
 * Evaluates whether a field should be hidden based on its showWhen condition.
 * @param showWhen - Condition path, optionally prefixed with "!" for negation
 * @param record - The record to evaluate against
 * @returns true if the field should be hidden, false if it should be shown
 */
const shouldHideField = (showWhen: string, record?: RaRecord<Identifier>): boolean => {
  const isNegated = showWhen.startsWith("!");
  const path = isNegated ? showWhen.slice(1) : showWhen;
  const val = getByPath(record, path);
  return isNegated ? !!val : !val;
};

/**
 * Renders a responsive grid of editable fields using metadata from allColumnsMetadata.json.
 * Fields can be grouped into sections using the `section` property.
 * If no section is provided on any field, a flat layout is rendered instead.
 * Automatically checks edit permissions for the resource and makes all fields read-only if user lacks permission.
 */
export const EditableGrid = ({
  fields,
  columnsPerRow = 5,
  isDrawerOpen = false,
  recordType,
  resource: resourceProp,
  defaultShowEmptyFields = false,
  sectionDescriptions,
}: EditableGridProps) => {
  const [showAllFields, setShowAllFields] = useState<boolean>(defaultShowEmptyFields);

  // Check edit permissions for this resource
  const { canEdit } = useResourcePermissions(recordType);

  const effectiveColumnsPerRow = isDrawerOpen ? Math.min(columnsPerRow, 3) : columnsPerRow;
  const lgUnit = 12 / effectiveColumnsPerRow;
  const mdUnit = 12 / 3;
  const smUnit = 12 / 2;
  const xsUnit = 12;

  const hasSections = fields.some((field) => field.section);
  const record = useRecordContext();
  const contextResource = useResourceContext();
  // Use prop if provided, otherwise fall back to context
  const resource = resourceProp || contextResource;

  // Group fields by their `section` property for visual separation in the grid.
  // Fields without a `section` are rendered in a flat layout outside this logic.
  const groupedFields = fields.reduce<Record<string, EditorField[]>>((acc, field) => {
    const group = field.section!;
    if (!acc[group]) acc[group] = [];
    acc[group].push(field);
    return acc;
  }, {});

  // Check if a section has any visible fields
  const sectionHasVisibleFields = (sectionFields: EditorField[]) => {
    return sectionFields.some((field) => {
      const valueExists = !!getByPath(record, field.source);
      // Don't count empty fields if user can't edit OR if showAllFields is false
      if (!valueExists && (!canEdit || !showAllFields)) return false;
      if (field.showWhen && shouldHideField(field.showWhen, record)) return false;
      return true;
    });
  };

  useEffect(() => {
    setShowAllFields(defaultShowEmptyFields);
  }, [defaultShowEmptyFields]);

  const getGridContent = (fields: EditorField[]) => {
    return (
      <Grid container spacing={2}>
        {fields.map((field, index) => {
          const {
            source,
            type = "text",
            label,
            md,
            sm,
            xs,
            lg,
            reference,
            displayField,
            showWhen,
            choices,
            readOnlyReason,
            filter,
            getOptionLabel,
            validate,
            numberFieldOptions,
          } = field;

          const sourceValueExists = !!getByPath(record, source);
          // Don't show empty fields if user can't edit OR if showAllFields is false
          if (!sourceValueExists && (!canEdit || !showAllFields)) return null;

          // Check showWhen condition - hide field if condition not met
          if (showWhen && shouldHideField(showWhen, record)) {
            return null;
          }

          const { label: metaLabel, title: metaTitle } = getProps(source, recordType);

          // Use the original type, pass readOnly prop instead of swapping to readonly type
          const EditorComponent = editorComponentMap[type];

          const finalLabel = label ?? metaLabel;

          const commonProps = {
            source,
            label: finalLabel,
            helperText: type === "dateRangeArray" ? undefined : metaTitle,
            resource,
            readOnly: !canEdit || !!readOnlyReason,
            readOnlyReason: canEdit ? readOnlyReason : "You don't have permission to edit",
            validate,
          };

          const editorProps =
            type === "reference" || type === "referenceArray"
              ? {
                  reference,
                  displayField,
                  filter,
                  getOptionLabel,
                  ...commonProps,
                }
              : type === "select"
                ? {
                    choices: choices || [],
                    ...commonProps,
                  }
                : type === "number"
                  ? {
                      numberFieldOptions,
                      ...commonProps,
                    }
                  : { ...commonProps };

          // dateRangeArray fields need more horizontal space
          const isWideField = type === "dateRangeArray";
          const defaultLg = isWideField ? Math.min(lgUnit * 2, 12) : lgUnit;
          const defaultMd = isWideField ? Math.min(mdUnit * 2, 12) : mdUnit;

          return (
            <Grid
              key={`${source}-${index}`}
              size={{
                xs: xs ?? xsUnit,
                sm: sm ?? smUnit,
                md: md ?? defaultMd,
                lg: lg ?? defaultLg,
              }}
              // minWidth:0 lets the cell shrink; overflowWrap (inherited) breaks
              // long unbroken values (emails/URLs) so they don't overlap the next column.
              sx={{ minWidth: 0, overflowWrap: "anywhere" }}
            >
              <EditorComponent {...editorProps} />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const hiddenFieldCount = fields.filter((f) => !getByPath(record, f.source)).length;

  return (
    <Box sx={{ p: 2 }}>
      {/* Only show empty fields toggle if user can edit */}
      {canEdit && hiddenFieldCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            gap: 2,
          }}
        >
          <Typography
            variant="caption"
            onClick={() => setShowAllFields(!showAllFields)}
            sx={{
              color: "text.secondary",
              cursor: "pointer",
              "&:hover": { color: "primary.main" },
            }}
          >
            {showAllFields ? "Hide empty fields" : `Show ${hiddenFieldCount} empty fields`}
          </Typography>
        </Box>
      )}
      {hasSections
        ? Object.entries(groupedFields).map(([sectionTitle, grouped]) => {
            // Hide section if no fields are visible (unless showAllFields is on)
            if (!showAllFields && !sectionHasVisibleFields(grouped)) return null;

            const hasHeader = sectionTitle && sectionTitle !== "undefined";

            return (
              <Box key={sectionTitle} sx={{ mb: 3 }}>
                {hasHeader && (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.75rem",
                      mb: 1.5,
                      pb: 0.75,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {sectionTitle}
                  </Typography>
                )}
                {hasHeader && sectionDescriptions?.[sectionTitle] && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block", // caption is inline by default, so the margins below need this
                      color: "text.secondary",
                      fontStyle: "italic",
                      mt: -0.75,
                      mb: 1.5,
                    }}
                  >
                    {sectionDescriptions[sectionTitle]}
                  </Typography>
                )}
                {getGridContent(grouped)}
              </Box>
            );
          })
        : getGridContent(fields)}
    </Box>
  );
};
